import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Opportunity, OpportunityFilters, OpportunityApplication } from '@/types/opportunity';

export const useOpportunities = () => {
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<OpportunityFilters>({});

  const fetchOpportunities = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('opportunities')
        .select('*')
        .in('status', ['active', 'pending', 'closed', 'archived'])
        .order('created_at', { ascending: false });

      if (filters.type && filters.type !== 'all') {
        query = query.eq('type', filters.type);
      }
      if (filters.category && filters.category !== 'all') {
        query = query.eq('category', filters.category);
      }
      if (filters.location) {
        query = query.ilike('location', `%${filters.location}%`);
      }
      if (filters.search) {
        query = query.or(`title.ilike.%${filters.search}%,description.ilike.%${filters.search}%,company.ilike.%${filters.search}%`);
      }
      if (filters.tags && filters.tags.length > 0) {
        query = query.overlaps('tags', filters.tags);
      }
      if (filters.is_premium !== undefined) {
        query = query.eq('is_premium', filters.is_premium);
      }

      const { data, error } = await query;

      if (error) throw error;
      setOpportunities((data || []) as Opportunity[]);
    } catch (error) {
      console.error('Error fetching opportunities:', error);
      toast.error('Erreur lors du chargement des opportunités');
    } finally {
      setLoading(false);
    }
  };

  const createOpportunity = async (opportunityData: Partial<Opportunity>) => {
    try {
      const user = await supabase.auth.getUser();
      
      // Utiliser upsert pour éviter les doublons basés sur le titre
      const { data, error } = await supabase
        .from('opportunities')
        .upsert({
          title: opportunityData.title || '',
          description: opportunityData.description || '',
          type: opportunityData.type || 'client',
          company: opportunityData.company || '',
          contact_email: opportunityData.contact_email || '',
          contact_name: opportunityData.contact_name,
          location: opportunityData.location,
          budget_range: opportunityData.budget_range,
          required_skills: opportunityData.required_skills,
          category: opportunityData.category,
          tags: opportunityData.tags,
          deadline: opportunityData.deadline,
          is_premium: opportunityData.is_premium || false,
          is_enterprise: opportunityData.is_enterprise || false,
          created_by: user.data.user?.id,
          status: 'active',
        }, { onConflict: 'title', ignoreDuplicates: false })
        .select()
        .single();

      if (error) throw error;
      
      setOpportunities(prev => [data as Opportunity, ...prev]);
      toast.success('Opportunité créée avec succès');

      // 🔥 PHASE 3: Analyse automatique des gaps après création
      if (data?.id && opportunityData.required_skills && opportunityData.required_skills.length > 0) {
        // Petite pause pour laisser le trigger s'exécuter
        setTimeout(async () => {
          const { data: gaps } = await supabase
            .from('opportunity_skill_gaps')
            .select('*, gap_severity')
            .eq('opportunity_id', data.id)
            .eq('gap_severity', 'critical');

          if (gaps && gaps.length > 0) {
            toast.warning(
              `⚠️ ${gaps.length} compétence(s) critique(s) manquent dans votre réseau`,
              {
                description: 'Voir les profils manquants dans Networking > Profils Manquants',
                duration: 8000,
              }
            );
          }
        }, 1500);
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error creating opportunity:', error);
      toast.error('Erreur lors de la création de l\'opportunité');
      return { data: null, error };
    }
  };

  const updateOpportunity = async (id: string, updates: Partial<Opportunity>) => {
    try {
      const { data, error } = await supabase
        .from('opportunities')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      
      setOpportunities(prev => prev.map(opp => opp.id === id ? data as Opportunity : opp));
      toast.success('Opportunité mise à jour');
      return { data, error: null };
    } catch (error) {
      console.error('Error updating opportunity:', error);
      toast.error('Erreur lors de la mise à jour');
      return { data: null, error };
    }
  };

  const deleteOpportunity = async (id: string) => {
    try {
      const { error } = await supabase
        .from('opportunities')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setOpportunities(prev => prev.filter(opp => opp.id !== id));
      toast.success('Opportunité supprimée');
    } catch (error) {
      console.error('Error deleting opportunity:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const archiveOpportunity = async (id: string) => {
    try {
      const { data, error } = await supabase
        .from('opportunities')
        .update({ status: 'archived' })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      
      setOpportunities(prev => prev.map(opp => opp.id === id ? data as Opportunity : opp));
      toast.success('Opportunité archivée');
      return { data, error: null };
    } catch (error) {
      console.error('Error archiving opportunity:', error);
      toast.error('Erreur lors de l\'archivage');
      return { data: null, error };
    }
  };

  const restoreOpportunity = async (id: string) => {
    try {
      const { data, error } = await supabase
        .from('opportunities')
        .update({ status: 'active' })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      
      setOpportunities(prev => prev.map(opp => opp.id === id ? data as Opportunity : opp));
      toast.success('Opportunité restaurée');
      return { data, error: null };
    } catch (error) {
      console.error('Error restoring opportunity:', error);
      toast.error('Erreur lors de la restauration');
      return { data: null, error };
    }
  };

  const applyToOpportunity = async (opportunityId: string, message?: string) => {
    try {
      const { data, error } = await supabase
        .from('opportunity_applications')
        .insert([{
          opportunity_id: opportunityId,
          applicant_id: (await supabase.auth.getUser()).data.user?.id,
          message
        }])
        .select()
        .single();

      if (error) throw error;
      
      toast.success('Candidature envoyée avec succès');
      return { data, error: null };
    } catch (error) {
      console.error('Error applying to opportunity:', error);
      toast.error('Erreur lors de l\'envoi de la candidature');
      return { data: null, error };
    }
  };

  const incrementViewCount = async (id: string) => {
    try {
      // First get current view count, then update
      const { data: current, error: fetchError } = await supabase
        .from('opportunities')
        .select('view_count')
        .eq('id', id)
        .single();
      
      if (fetchError) throw fetchError;
      
      // Skip view count since column doesn't exist
      console.log('Track opportunity view:', id);
      
      // Skip the update since view_count doesn't exist - just update local state
    } catch (error) {
      console.error('Error incrementing view count:', error);
    }
  };

  useEffect(() => {
    fetchOpportunities();
  }, [filters]);

  const bulkUpdateOpportunities = async (ids: string[], updates: Partial<Opportunity>) => {
    try {
      const { data, error } = await supabase
        .from('opportunities')
        .update(updates)
        .in('id', ids)
        .select();

      if (error) throw error;
      
      setOpportunities(prev => 
        prev.map(opp => 
          ids.includes(opp.id) ? { ...opp, ...updates } : opp
        )
      );
      
      toast.success(`${ids.length} opportunité${ids.length > 1 ? 's' : ''} mise${ids.length > 1 ? 's' : ''} à jour`);
      return { data, error: null };
    } catch (error) {
      console.error('Error bulk updating opportunities:', error);
      toast.error('Erreur lors de la mise à jour en masse');
      return { data: null, error };
    }
  };

  const bulkDeleteOpportunities = async (ids: string[]) => {
    try {
      const { error } = await supabase
        .from('opportunities')
        .delete()
        .in('id', ids);

      if (error) throw error;
      
      setOpportunities(prev => prev.filter(opp => !ids.includes(opp.id)));
      toast.success(`${ids.length} opportunité${ids.length > 1 ? 's' : ''} supprimée${ids.length > 1 ? 's' : ''}`);
    } catch (error) {
      console.error('Error bulk deleting opportunities:', error);
      toast.error('Erreur lors de la suppression en masse');
    }
  };

  const bulkArchiveOpportunities = async (ids: string[]) => {
    try {
      const { data, error } = await supabase
        .from('opportunities')
        .update({ status: 'archived' })
        .in('id', ids)
        .select();

      if (error) throw error;
      
      setOpportunities(prev => 
        prev.map(opp => 
          ids.includes(opp.id) ? { ...opp, status: 'archived' } : opp
        )
      );
      
      toast.success(`${ids.length} opportunité${ids.length > 1 ? 's' : ''} archivée${ids.length > 1 ? 's' : ''}`);
      return { data, error: null };
    } catch (error) {
      console.error('Error bulk archiving opportunities:', error);
      toast.error('Erreur lors de l\'archivage en masse');
      return { data: null, error };
    }
  };

  return {
    opportunities,
    loading,
    filters,
    setFilters,
    createOpportunity,
    updateOpportunity,
    deleteOpportunity,
    archiveOpportunity,
    restoreOpportunity,
    applyToOpportunity,
    incrementViewCount,
    bulkUpdateOpportunities,
    bulkDeleteOpportunities,
    bulkArchiveOpportunities,
    refetch: fetchOpportunities
  };
};